import { initializeApp } from "firebase/app"
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check"
import {
	connectFirestoreEmulator,
	initializeFirestore,
	persistentLocalCache,
	persistentMultipleTabManager,
} from "firebase/firestore"
import { connectAuthEmulator, getAuth } from "firebase/auth"
import { connectFunctionsEmulator, getFunctions, httpsCallable } from "firebase/functions"
import { requireNetwork } from "../utils/network.utils"

const firebaseConfig = {
	apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
	authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
	projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
	storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
	messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
	appId: process.env.REACT_APP_FIREBASE_APP_ID,
}

const app = initializeApp(firebaseConfig)

// ── APP CHECK ──────────────────────────────────────────────────────
// Dimostra che le richieste arrivano da questa app e non da uno script:
// da quando ci si registra liberamente, senza non c'è niente che impedisca
// a un bot di aprire account a raffica.
//
// Si accende dando la chiave del sito: finché la variabile non c'è, l'app
// funziona esattamente come prima. Va acceso qui *prima* di pretenderlo in
// console, altrimenti l'app in produzione smette di leggere.
//
// Usiamo reCAPTCHA Enterprise perché Google ha deprecato il v3 classico:
// cambia il fornitore, non il resto — la chiave del sito arriva sempre da
// una variabile d'ambiente.
const appCheckSiteKey = process.env.REACT_APP_APPCHECK_SITE_KEY

if (appCheckSiteKey) {
	// in sviluppo il browser non passa da reCAPTCHA: si usa il token di
	// debug che la console accetta per quella sola macchina
	if (process.env.REACT_APP_APPCHECK_DEBUG_TOKEN) {
		window.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN
	}

	initializeAppCheck(app, {
		provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
		isTokenAutoRefreshEnabled: true,
	})
}
// ── CACHE LOCALE ───────────────────────────────────────────────────
// Le scritture fatte senza rete restano in coda finché il server non le
// conferma: la coda deve stare su disco (IndexedDB), altrimenti ricaricando
// l'app o chiudendola prima che torni la rete va persa, e la lezione "salvata"
// sparisce. La cache si passa a `initializeFirestore`: messa nella config di
// `initializeApp` viene ignorata e Firestore resta con la cache in memoria.
//
// Il gestore multi-scheda lascia l'app aperta in più schede senza che la
// seconda perda la persistenza.
export const firestoreDB = initializeFirestore(app, {
	localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
})
export const auth = getAuth(app)
export const functions = getFunctions(app, "us-central1")

// ── EMULATORI ──────────────────────────────────────────────────────
// I test end-to-end (`yarn e2e`, cartella `e2e/`) girano contro gli
// emulatori di Firebase: accessi, dati e functions locali, che nascono
// vuoti e spariscono alla fine. Senza la variabile l'app parla col
// progetto della configurazione, come sempre.
if (process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true") {
	connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true })
	connectFirestoreEmulator(firestoreDB, "127.0.0.1", 8080)
	connectFunctionsEmulator(functions, "127.0.0.1", 5001)
}

// Le Cloud Functions girano sul server e senza rete non c'è niente da
// mettere in coda: si dice subito che serve la connessione. Lasciate
// andare, rispondono `internal` dopo un po' — lo stesso errore di un
// guasto vero — e chi le chiama non saprebbe cosa dire.
export const callFunction = name => {
	const callable = httpsCallable(functions, name)
	return async data => {
		requireNetwork()
		return callable(data)
	}
}
